import { useCallback, useEffect, useContext, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash'
import { PendingRow } from '@components/dataset/pending/types'
import { submissionContext } from '@contexts'
import { setDatasetPendingRollup } from '@datasets/datasets.actions'
import {
  getDatasetPending,
  getPendingRollup,
  getPendingDatasetGlobal,
  getPendingRollupGlobal,
} from '@global/api'
import { useQueryPendingRollup } from '@global/hooks/useQueryPendingRollup'
import { useAppDispatch } from '@redux/hooks'

interface IArgs {
  templateId: number
  onSuccess: (data: PendingRow[]) => void
}

/**
 * Pending submissions hook.
 * Set submission context state for submissions into byId object.
 * Fetch pending submissions.
 */
export const usePendingSubmissions = ({ templateId }: IArgs) => {
  const queryClient = useQueryClient()
  const { setById } = useContext(submissionContext)

  const {
    data: datasetPendingData = [],
    isLoading: datasetPendingIsLoading,
    error,
  } = useQuery({
    queryKey: ['dataset-pending', templateId],
    queryFn: ({ signal }) => getDatasetPending({ templateId: String(templateId), signal }),
    refetchOnWindowFocus: false,
  })

  const { data: pendingRollupData = [], isLoading: pendingRollupIsLoading } = useQuery({
    queryKey: ['pending-rollup', templateId],
    queryFn: ({ signal }) => getPendingRollup({ templateId: String(templateId), signal }),
    refetchOnWindowFocus: false,
  })

  const isLoading = datasetPendingIsLoading || pendingRollupIsLoading

  const pendingSubmissions = useMemo(() => {
    const uniqueMap = Object.fromEntries(
      [...datasetPendingData, ...pendingRollupData].map(item => [item.id, item]),
    )
    return Object.values(uniqueMap)
  }, [datasetPendingData, pendingRollupData])

  const byIdReady = useMemo(
    () =>
      pendingSubmissions.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.id]: curr,
        }),
        {},
      ),
    [pendingSubmissions],
  )

  useEffect(() => {
    if (isLoading) return
    if (pendingSubmissions.length === 0) return

    setById(prevData => {
      const updated = { ...prevData, ...byIdReady }
      // Only update if data has changed, otherwise it will cause an infinite loop.
      return isEqual(prevData, byIdReady) ? prevData : updated
    })
  }, [isLoading, byIdReady, pendingSubmissions])

  const invalidateDatasetPending = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['dataset-pending', templateId] })
    void queryClient.invalidateQueries({ queryKey: ['pending-rollup', templateId] })
  }, [queryClient, templateId])

  return {
    pendingSubmissions,
    invalidateDatasetPending,
    isLoading,
    error,
  }
}

export const useGlobalPendingSubmissions = () => {
  const queryClient = useQueryClient()
  const KEY = ['global-pending']

  const {
    data: pendingSubmissions,
    isLoading,
    error,
  } = useQuery({
    queryKey: KEY,
    queryFn: async ({ signal }) => {
      const globalData = await getPendingDatasetGlobal({ signal })

      const pendingData = await getPendingRollupGlobal({ signal })
      return [...globalData, ...pendingData]
    },
    refetchOnWindowFocus: false,
  })

  const invalidateGlobalPending = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: KEY })
  }, [queryClient])

  return {
    pendingSubmissions,
    invalidateGlobalPending,
    isLoading,
    error,
  }
}

/**
 * Fetch pending rollup dataset data.
 * - if feature flag is enabled, otherwise skip query.
 * - Add result data rows to submissionContext byID Object, and to Redux store.
 * @param param0
 * @returns
 */
export const useQueryPendingRollupDataset = ({ datasetId }: { datasetId: string }) => {
  const dispatch = useAppDispatch()
  const { setById } = useContext(submissionContext)
  const {
    data,
    isInitialLoading: isLoading,
    error,
  } = useQueryPendingRollup({
    datasetId,
    onSuccess: result => {
      setById((prevData: object) => ({ ...prevData, [result.id]: result }))
      dispatch(setDatasetPendingRollup(result))
    },
  })

  return {
    pendingRollupDataset: data,
    isLoading,
    error,
  }
}
